import React, { useMemo } from 'react';

/**
 * LegalDocBody — renders a long-form legal document from a single
 * template string. Parses the source line-by-line with rules tuned to
 * the way our exported .docx contracts are structured:
 *
 *   • A line that matches /^[A-Z][A-Z ,&/-]+$/ becomes an `<h3>` (a
 *     definition label inside Section 1, e.g., "Account", "Customer").
 *   • A line that matches /^\d+\.\s+[A-Z…]/ (e.g. "1. THE SERVICE") is
 *     rendered as a top-level `<h2>` (section heading).
 *   • A line that matches /^\d+\.\d+\s+/ (e.g. "1.1 ...") is rendered
 *     as a paragraph with a bold numeric prefix.
 *   • A line starting with "(a) " / "(b) " / etc. is rendered as a
 *     bulleted-style sub-paragraph indented under the previous block.
 *   • A line beginning with "|" (markdown table row) accumulates into
 *     a styled table.
 *   • A line beginning with "[[CONFIRM:" is wrapped in a subdued
 *     in-line marker so it stays visible (these are intentional
 *     drafting placeholders that the document owner still needs to
 *     fill in).
 *   • Blank lines produce paragraph separators.
 *   • Everything else falls through as a plain `<p>`.
 *
 * The parser keeps the rendered DOM clean (no nested ul/ol/table
 * mismatches) by walking the lines once and emitting React children
 * from a small state machine.
 */

const CONFIRM_RE = /\[\[CONFIRM:[^\]]*\]\]/g;

function renderInline(text, keyPrefix = '') {
  // Split on the [[CONFIRM: ...]] markers so each becomes a styled span.
  const parts = [];
  let lastIdx = 0;
  let match;
  let i = 0;
  const re = new RegExp(CONFIRM_RE.source, 'g');
  while ((match = re.exec(text)) !== null) {
    if (match.index > lastIdx) {
      parts.push(text.slice(lastIdx, match.index));
    }
    parts.push(
      <span
        key={`${keyPrefix}-c-${i++}`}
        className="inline rounded px-1 py-[1px] bg-amber-500/10 text-amber-200/95 border border-amber-500/30 text-[0.92em] font-mono"
        title="Drafting placeholder — to be confirmed before publication"
      >
        {match[0]}
      </span>
    );
    lastIdx = re.lastIndex;
  }
  if (lastIdx < text.length) parts.push(text.slice(lastIdx));
  return parts.length === 1 && typeof parts[0] === 'string' ? text : parts;
}

function classify(line) {
  const trimmed = line.trim();
  if (!trimmed) return { kind: 'blank' };
  if (trimmed.startsWith('|')) return { kind: 'tableRow', text: trimmed };
  if (/^\d+\.\s+[A-Z]/.test(trimmed) && trimmed === trimmed.toUpperCase().replace(/[^A-Z0-9\s.,&/-]/g, '')) {
    // e.g. "1. THE SERVICE"
    return { kind: 'h2', text: trimmed };
  }
  if (/^\d+\.\s+[A-Z]/.test(trimmed)) {
    return { kind: 'h2', text: trimmed };
  }
  if (/^\d+\.\d+(\.\d+)?\s+/.test(trimmed)) {
    const m = trimmed.match(/^(\d+\.\d+(?:\.\d+)?)\s+(.*)$/);
    return { kind: 'numbered', num: m[1], text: m[2] };
  }
  if (/^\([a-z]\)\s+/.test(trimmed)) {
    return { kind: 'sub', text: trimmed };
  }
  // Single-word or short uppercase label (definition like "Account")
  if (trimmed.length <= 40 && /^[A-Z][A-Za-z0-9 /&'.-]+$/.test(trimmed) && trimmed === trimmed[0].toUpperCase() + trimmed.slice(1) && !trimmed.endsWith('.')) {
    return { kind: 'defterm', text: trimmed };
  }
  return { kind: 'p', text: trimmed };
}

export default function LegalDocBody({ source }) {
  const blocks = useMemo(() => {
    const lines = source.replace(/\r\n/g, '\n').split('\n');
    const out = [];
    let tableBuffer = null;
    const flushTable = () => {
      if (!tableBuffer) return;
      const rows = tableBuffer
        .map((row) => row.replace(/^\|/, '').replace(/\|$/, '').split('|').map((c) => c.trim()))
        .filter((r) => r.length > 0 && !r.every((c) => /^-+$/.test(c) || c === ''));
      if (rows.length > 0) {
        out.push({ kind: 'table', rows });
      }
      tableBuffer = null;
    };
    for (const raw of lines) {
      const c = classify(raw);
      if (c.kind === 'tableRow') {
        tableBuffer = tableBuffer || [];
        tableBuffer.push(c.text);
        continue;
      }
      flushTable();
      out.push(c);
    }
    flushTable();
    return out;
  }, [source]);

  return (
    <>
      {blocks.map((b, i) => {
        switch (b.kind) {
          case 'blank':
            return null;
          case 'h2':
            return (
              <h2
                key={`b-${i}`}
                className="scroll-mt-20 text-xl sm:text-2xl font-black uppercase tracking-tight text-white mt-12 mb-4 pb-2 border-b border-gold-900/30"
              >
                {renderInline(b.text, `h2-${i}`)}
              </h2>
            );
          case 'numbered':
            return (
              <p key={`b-${i}`} className="text-white/75 leading-relaxed">
                <span className="text-gold-400 font-bold mr-2">{b.num}</span>
                {renderInline(b.text, `n-${i}`)}
              </p>
            );
          case 'sub':
            return (
              <p key={`b-${i}`} className="text-white/70 leading-relaxed pl-6">
                {renderInline(b.text, `s-${i}`)}
              </p>
            );
          case 'defterm':
            return (
              <p
                key={`b-${i}`}
                className="text-gold-300 font-bold mt-4 mb-0"
              >
                {renderInline(b.text, `d-${i}`)}
              </p>
            );
          case 'table':
            return (
              <div key={`b-${i}`} className="overflow-x-auto my-6">
                <table className="min-w-full border border-gold-900/30 text-sm">
                  <thead className="bg-gold-500/[0.06]">
                    <tr>
                      {b.rows[0].map((cell, j) => (
                        <th
                          key={j}
                          className="px-3 py-2 text-left text-gold-300 font-bold uppercase tracking-wide text-xs border-b border-gold-900/30"
                        >
                          {renderInline(cell, `th-${i}-${j}`)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {b.rows.slice(1).map((row, ri) => (
                      <tr key={ri} className="border-b border-gold-900/20 last:border-0">
                        {row.map((cell, ci) => (
                          <td key={ci} className="px-3 py-2 align-top text-white/75">
                            {renderInline(cell, `td-${i}-${ri}-${ci}`)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          case 'p':
          default:
            return (
              <p key={`b-${i}`} className="text-white/75 leading-relaxed">
                {renderInline(b.text, `p-${i}`)}
              </p>
            );
        }
      })}
    </>
  );
}
